import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import Signup from './pages/Signup';
import Login from './pages/Login';
import Verify from './pages/Verify';

import Onboarding from './pages/Onboarding';
import Hipaa from './pages/Hipaa';
import FamilyMembers from './pages/FamilyMembers';
import FamilyHipaa from './pages/FamilyHipaa';
import HipaaSignPublic from './pages/HipaaSignPublic';

/* Legacy Dashboard.tsx is intentionally left in the codebase but no
   longer routed, kept as a rollback path. v3 routes /dashboard to
   DashboardRedirect which forwards to the customer's most recent
   /case/:id (Phase 4r2). */
// import Dashboard from './pages/Dashboard';
import DashboardRedirect from './pages/DashboardRedirect';
import Bills from './pages/Bills';
import AddBill from './pages/AddBill';
import Plan from './pages/Plan';
import Contact from './pages/Contact';
import Account from './pages/Account';
import HowItWorks from './pages/HowItWorks';
import EobReview from './pages/EobReview';
import ActionPlan from './pages/ActionPlan';
import CasePage from './pages/CasePage';
import DraftCase from './pages/DraftCase';

import AppGate from './routes/AppGate';
import Start from './pages/Start';
import AccountGate from './routes/AccountGate';
import AppSidebar from './components/AppSidebar';
import SettingsLayout from './components/SettingsLayout';
import { ToastProvider } from './components/context/ToastContext';
import { AuthProvider } from './hooks/useAuth';
import PageTransition from './components/PageTransition';
import ErrorBoundary from './components/ErrorBoundary';
import IntercomProvider from './components/IntercomProvider';
import AskProvider from './components/AskProvider';
import Ask from './pages/Ask';
import PostHogIdentifier from './components/PostHogIdentifier';
import ActivityHeartbeat from './components/ActivityHeartbeat';

/* /dashboard was Home's address until Oct 2026. Emails, texts and the
   server still link to /dashboard?case=<id>, so it forwards to / and keeps
   the query string (DashboardRedirect opens the case from there). */
const DashboardAlias = () => {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: '/', search, hash }} replace />;
};

const ExternalRedirect = ({ to }: { to: string }) => {
  window.location.replace(to);
  return null;
};

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
    <PostHogIdentifier>
    <IntercomProvider>
    <AskProvider>
    <ToastProvider>
    <ActivityHeartbeat />
    <PageTransition>
    <Routes>
      {/* Home (Oct 2026). Signed out, the gate sends you to /login. Signed
          in, where you land is decided in one place: the "where should
          you land" step in AppGate. */}
      <Route
        path="/"
        element={
          <AppGate>
            <DashboardRedirect />
          </AppGate>
        }
      />
      <Route path="/dashboard" element={<DashboardAlias />} />

      {/* Public */}
      <Route path="/signup" element={<Signup />} />
      <Route path="/login" element={<Login />} />
      <Route path="/verify" element={<Verify />} />
      <Route path="/start" element={<Start />} />

      {/* Journey pages */}
      <Route
        path="/onboarding"
        element={
          <AccountGate>
            <Onboarding />
          </AccountGate>
        }
      />
      {/* App (fully gated) */}
      <Route
        path="/bills"
        element={
          <AppGate>
            <AppSidebar><Bills /></AppSidebar>
          </AppGate>
        }
      />
      {/* v3 single-case workspace (Phase 4r2). CasePage renders its own
          AppSidebar internally so we don't double-wrap. Inner ErrorBoundary
          isolates blow-ups to this route — if one malformed bill row throws
          inside CasePage, the user can still navigate to /bills, /plan,
          etc. without a full app reload. */}
      <Route
        path="/case/:id"
        element={
          <AppGate>
            <ErrorBoundary>
              <CasePage />
            </ErrorBoundary>
          </AppGate>
        }
      />
      {/* A bill uploaded on a free account, before it's paid for. Laid out
          like a case so they can see what they'd be starting. */}
      <Route
        path="/case/draft"
        element={
          <AppGate>
            <ErrorBoundary>
              <DraftCase />
            </ErrorBoundary>
          </AppGate>
        }
      />
      <Route
        path="/add-bill"
        element={
          <AppGate>
            <AppSidebar><AddBill /></AppSidebar>
          </AppGate>
        }
      />
      <Route
        path="/plan"
        element={
          <AppGate>
            <AppSidebar><SettingsLayout><Plan /></SettingsLayout></AppSidebar>
          </AppGate>
        }
      />
      {/* Settings (Oct 2026): Account, Plan, Family and Contact open inside
          one two-pane Settings page. /settings on its own is the list. */}
      {/* Ask Agent Loop, the AI helper (Oct 2026). A full page, like Ask Granted. */}
      <Route
        path="/ask"
        element={
          <AppGate>
            <AppSidebar><Ask /></AppSidebar>
          </AppGate>
        }
      />
      <Route
        path="/settings"
        element={
          <AppGate>
            <AppSidebar><SettingsLayout /></AppSidebar>
          </AppGate>
        }
      />
      <Route
        path="/account"
        element={
          <AppGate>
            <AppSidebar><SettingsLayout><Account /></SettingsLayout></AppSidebar>
          </AppGate>
        }
      />
      <Route
        path="/how-it-works"
        element={
          <AppGate>
            <AppSidebar><HowItWorks /></AppSidebar>
          </AppGate>
        }
      />
      <Route
        path="/contact"
        element={
          <AppGate>
            <AppSidebar><SettingsLayout><Contact /></SettingsLayout></AppSidebar>
          </AppGate>
        }
      />
      <Route
        path="/hipaa"
        element={
          <AppGate>
            <AppSidebar><Hipaa /></AppSidebar>
          </AppGate>
        }
      />

      {/* Family member management */}
      <Route
        path="/family-members"
        element={
          <AppGate>
            <AppSidebar><SettingsLayout><FamilyMembers /></SettingsLayout></AppSidebar>
          </AppGate>
        }
      />
      <Route
        path="/hipaa/family/:familyMemberId"
        element={
          <AppGate>
            <AppSidebar><FamilyHipaa /></AppSidebar>
          </AppGate>
        }
      />

      {/* Public spouse signing, NO auth gate */}
      <Route path="/hipaa/sign/:token" element={<HipaaSignPublic />} />

      {/* EOB Review. No longer sold (2026-08-28) but existing EOB accounts
          still land here, so the route stays. */}
      <Route
        path="/eob"
        element={
          <AppGate>
            <AppSidebar><EobReview /></AppSidebar>
          </AppGate>
        }
      />

      {/* Personalized Action Plan: upload a bill, get a written negotiation plan. */}
      <Route
        path="/action-plan"
        element={
          <AppGate>
            <AppSidebar><ActionPlan /></AppSidebar>
          </AppGate>
        }
      />

      {/* Legacy redirects */}
      <Route path="/billing" element={<Navigate to="/plan" replace />} />

      {/* Catch-all */}
      <Route
        path="*"
        element={<ExternalRedirect to="https://mediloop.ai/404" />}
      />
    </Routes>
    </PageTransition>
    </ToastProvider>
    </AskProvider>
    </IntercomProvider>
    </PostHogIdentifier>
    </AuthProvider>
    </ErrorBoundary>
  );
}
